import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { Loader2, Mail } from 'lucide-react';

interface InviteTarget {
  id: number;
  name: string;
}

interface PendingInvite {
  id: number;
  client_id: number;
  client_alias: string;
  role: 'manager' | 'user';
  practice_scope: 'all' | 'none' | 'selected';
  aco_scope: 'all' | 'none' | 'selected';
  inviter_user_id: number;
  inviter_name: string;
  created_at: string;
  expires_at: string;
  practices: InviteTarget[];
  acos: InviteTarget[];
}

function scopeSummary(invite: PendingInvite): string {
  const parts: string[] = [];
  if (invite.practice_scope === 'all') parts.push('All practices');
  else if (invite.practice_scope === 'selected') {
    parts.push(invite.practices.map((p) => p.name).join(', ') || `${invite.practices.length} practice(s)`);
  }
  if (invite.aco_scope === 'all') parts.push('All ACOs');
  else if (invite.aco_scope === 'selected') {
    parts.push(`ACO: ${invite.acos.map((a) => a.name).join(', ')}`);
  }
  return parts.join(' · ') || 'No access';
}

function daysLeft(expiresAt: string): number {
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)));
}

/**
 * Pending access invitations (Home). Consent gate: to accept, the invitee must
 * type the inviter's account email — verified server-side; the API deliberately
 * never returns the inviter's email.
 *
 * When multiple pending invites share the same inviter, Accept all confirms once.
 */
export function PendingInvitations() {
  const { isAuthenticated, refreshClients } = useAuth();
  const { toast } = useToast();
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [accepting, setAccepting] = useState<PendingInvite | null>(null);
  const [acceptingBatch, setAcceptingBatch] = useState<PendingInvite[] | null>(null);
  const [inviterEmail, setInviterEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const loadInvites = useCallback(async () => {
    try {
      const data = await apiClient.get('/api/invites/mine');
      setInvites(data.invites || []);
    } catch {
      // Silent: the card simply doesn't render if the fetch fails.
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) loadInvites();
  }, [isAuthenticated, loadInvites]);

  /** Largest same-inviter group (for Accept all). */
  const largestSameInviterGroup = useMemo(() => {
    if (invites.length < 2) return null;
    const byInviter = new Map<number, PendingInvite[]>();
    for (const inv of invites) {
      const list = byInviter.get(inv.inviter_user_id) || [];
      list.push(inv);
      byInviter.set(inv.inviter_user_id, list);
    }
    let best: PendingInvite[] | null = null;
    for (const group of byInviter.values()) {
      if (group.length >= 2 && (!best || group.length > best.length)) best = group;
    }
    return best;
  }, [invites]);

  const handleAccept = async () => {
    if (!accepting) return;
    setBusy(true);
    try {
      await apiClient.post(`/api/invites/${accepting.id}/accept`, { inviterEmail: inviterEmail.trim() });
      toast({
        title: 'Invitation accepted',
        description: `You now have access to ${accepting.client_alias}.`,
      });
      setAccepting(null);
      setInviterEmail('');
      await refreshClients();
      await loadInvites();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not accept invitation',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleAcceptBatch = async () => {
    if (!acceptingBatch || acceptingBatch.length === 0) return;
    setBusy(true);
    try {
      const result = await apiClient.post('/api/invites/accept-batch', {
        inviteIds: acceptingBatch.map((i) => i.id),
        inviterEmail: inviterEmail.trim(),
      });
      const count = result.count ?? acceptingBatch.length;
      toast({
        title: 'Invitations accepted',
        description: `You now have access to ${count} organization${count === 1 ? '' : 's'}.`,
      });
      setAcceptingBatch(null);
      setInviterEmail('');
      await refreshClients();
      await loadInvites();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not accept invitations',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async (invite: PendingInvite) => {
    try {
      await apiClient.post(`/api/invites/${invite.id}/decline`);
      toast({ title: 'Invitation declined' });
      await loadInvites();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Could not decline invitation',
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    }
  };

  if (invites.length === 0) return null;

  const dialogOpen = !!accepting || !!acceptingBatch;
  const batchMode = !!acceptingBatch;
  const dialogInviterName = acceptingBatch?.[0]?.inviter_name ?? accepting?.inviter_name;
  const dialogOrgLabel = batchMode
    ? `${acceptingBatch!.length} organizations`
    : accepting?.client_alias;

  return (
    <>
      <Card className="mb-8 border-primary/40 bg-primary/5">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Mail className="h-5 w-5 text-primary" />
                Pending invitations
              </CardTitle>
              <CardDescription>
                You have been invited to collaborate on the following organization{invites.length > 1 ? 's' : ''}.
              </CardDescription>
            </div>
            {largestSameInviterGroup && (
              <Button
                size="sm"
                onClick={() => {
                  setAccepting(null);
                  setAcceptingBatch(largestSameInviterGroup);
                  setInviterEmail('');
                }}
              >
                {largestSameInviterGroup.length === invites.length
                  ? `Accept all (${largestSameInviterGroup.length})`
                  : `Accept all from ${largestSameInviterGroup[0].inviter_name} (${largestSameInviterGroup.length})`}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {invites.map((invite) => (
            <div
              key={invite.id}
              className="flex flex-col gap-3 rounded-md border bg-background p-4 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{invite.client_alias}</span>
                  <Badge variant={invite.role === 'manager' ? 'default' : 'secondary'}>
                    {invite.role}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">
                  Invited by {invite.inviter_name} · {scopeSummary(invite)}
                </p>
                <p className="text-xs text-muted-foreground">
                  Expires in {daysLeft(invite.expires_at)} day{daysLeft(invite.expires_at) === 1 ? '' : 's'}
                </p>
              </div>
              <div className="flex flex-shrink-0 gap-2">
                <Button
                  size="sm"
                  onClick={() => {
                    setAcceptingBatch(null);
                    setAccepting(invite);
                    setInviterEmail('');
                  }}
                >
                  Accept
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDecline(invite)}>
                  Decline
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setAccepting(null);
            setAcceptingBatch(null);
            setInviterEmail('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{batchMode ? 'Accept invitations' : 'Accept invitation'}</DialogTitle>
            <DialogDescription>
              {batchMode ? (
                <>
                  To confirm invitations to <strong>{dialogOrgLabel}</strong> from{' '}
                  <strong>{dialogInviterName}</strong>, enter their email address.
                  {acceptingBatch && acceptingBatch.length <= 8 && (
                    <ul className="mt-2 list-inside list-disc text-muted-foreground">
                      {acceptingBatch.map((i) => (
                        <li key={i.id}>{i.client_alias}</li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <>
                  To confirm this invitation to <strong>{accepting?.client_alias}</strong>, enter the
                  email address of the person who invited you ({accepting?.inviter_name}).
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="inviter-email">Inviter's email address</Label>
            <Input
              id="inviter-email"
              type="email"
              placeholder="name@example.com"
              value={inviterEmail}
              onChange={(e) => setInviterEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && inviterEmail.trim() && !busy) {
                  if (batchMode) handleAcceptBatch();
                  else handleAccept();
                }
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAccepting(null);
                setAcceptingBatch(null);
              }}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              onClick={batchMode ? handleAcceptBatch : handleAccept}
              disabled={!inviterEmail.trim() || busy}
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {batchMode
                ? `Confirm & accept ${acceptingBatch!.length}`
                : 'Confirm & accept'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
